import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/ui/Button';
import { ServiceItem, FeatureItem } from '../../types/components';

const CompanyServices: FC = () => {
  const services: ServiceItem[] = [
    {
      title: 'Basic Hiring',
      description: 'Essential tools to find and hire great talent for your team.',
      features: ['Post up to 5 jobs', 'Access candidate database', 'Basic analytics', 'Email support'],
      price: 'FREE',
      popular: true
    },
    {
      title: 'Pro Hiring',
      description: 'Advanced features for growing companies with higher hiring needs.',
      features: ['Unlimited job posts', 'Priority candidate matching', 'Advanced analytics', 'Phone support', 'Team collaboration'],
      price: '₹2,999/month',
      popular: false
    }
  ];

  const features: FeatureItem[] = [
    { icon: '', title: 'Verified Candidates', description: 'All candidates are thoroughly verified to ensure quality and authenticity.' },
    { icon: '', title: 'Smart Filtering', description: 'AI-powered filters help you find the perfect candidates quickly.' },
    { icon: '', title: 'Team Collaboration', description: 'Work together with your hiring team to make the best decisions.' },
    { icon: '', title: 'Analytics Dashboard', description: 'Track your hiring metrics and optimize your recruitment process.' },
    { icon: '', title: 'Mobile Ready', description: 'Manage your hiring process on any device, anywhere.' },
    { icon: '', title: '24/7 Support', description: 'Get help whenever you need it with our dedicated support team.' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-primary-teaGreen/5 to-white">
      {/* Hero */}
      <section className="relative overflow-hidden pt-20 md:pt-24 pb-16">
        <div className="absolute -top-20 -right-20 h-64 w-64 md:h-96 md:w-96 rounded-full bg-primary-teaGreen/20 blur-3xl animate-pulse" />
        <div className="absolute -bottom-16 -left-16 h-64 w-64 md:h-96 md:w-96 rounded-full bg-primary-lightGreen/15 blur-3xl animate-pulse" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-primary-teaGreen/30 text-primary-black mb-6 border border-primary-teaGreen/40">Services for Companies</span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-primary-black mb-6 leading-tight">
              <span className="text-primary-lightGreen">FREE</span> for Companies
            </h1>
            <p className="text-lg md:text-xl lg:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto leading-relaxed">
              Build your dream team with verified candidates, smart filtering, and comprehensive hiring tools. Start hiring today at no cost.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register?isCompany=true">
                <Button size="lg" className="px-8 py-4 text-lg">Start Hiring Free</Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" size="lg" className="px-8 py-4 text-lg">Talk to Sales</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Free Section */}
      <section className="py-16 md:py-20 bg-white/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-black mb-4">Why is it FREE for Companies?</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We believe great companies deserve access to great talent without barriers. Our free tier gives you everything you need to start building your team.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-lightGreen/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-lightGreen" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-primary-black mb-2">No Hidden Costs</h3>
              <p className="text-gray-600">Truly free with no setup fees, no monthly charges, and no hidden costs.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-lightGreen/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-lightGreen" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-primary-black mb-2">Start Immediately</h3>
              <p className="text-gray-600">Get started in minutes with instant access to our candidate database.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-lightGreen/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-lightGreen" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-primary-black mb-2">Quality Candidates</h3>
              <p className="text-gray-600">Access to verified, high-quality candidates without any compromise on quality.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Free Features */}
      <section className="py-16 md:py-24 bg-white/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-black mb-4">What you get for FREE</h2>
            <p className="text-lg text-gray-600">Everything you need to start hiring great talent</p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg border border-primary-lightGreen/20">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-lightGreen to-primary-teaGreen rounded-2xl mb-6">
                  <svg className="w-10 h-10 text-primary-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-3xl font-bold text-primary-black mb-2">Complete Hiring Solution</h3>
                <p className="text-lg text-gray-600 mb-6">Access our full platform with no hidden costs or limitations</p>
                <div className="text-5xl font-extrabold text-primary-lightGreen mb-2">FREE</div>
                <p className="text-gray-600"></p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-7 gap-6 mb-8">
                <div></div>
                <div className="space-y-4 col-span-2">
                  <h4 className="text-xl  font-semibold text-primary-black mb-4">Core Features</h4>
                  <ul className="space-y-3">
                     <li className="flex">
                      <svg className="w-5 h-5 text-primary-lightGreen mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">Zero Setup Fees</span>
                    </li>
                    <li className="flex">
                      <svg className="w-5 h-5 text-primary-lightGreen mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">AI-powered candidate matching</span>
                    </li>
                    <li className="flex">
                      <svg className="w-5 h-5 text-primary-lightGreen mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">Basic analytics dashboard</span>
                    </li>
                  </ul>
                </div>
                
                <div></div>
                <div className="space-y-4 col-span-2">
                  <h4 className="text-xl  font-semibold text-primary-black mb-4">Support & Tools</h4>
                  <ul className="space-y-3">
                    <li className="flex">
                      <svg className="w-5 h-5 text-primary-lightGreen mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">Email support</span>
                    </li>
                    <li className="flex">
                      <svg className="w-5 h-5 text-primary-lightGreen mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">Company Profile Setup</span>
                    </li>
                    <li className="flex">
                      <svg className="w-5 h-5 text-primary-lightGreen mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">Dedicated Dashboard</span>
                    </li>
                   
                  </ul>
                </div>
              </div>
              
              <div className="text-center">
                <Link to="/register?isCompany=true">
                  <Button size="lg" className="px-12 py-4 text-lg bg-primary-lightGreen text-primary-black hover:bg-primary-teaGreen">
                    Start Hiring Free
                  </Button>
                </Link>
                <p className="text-sm text-gray-600 mt-4">Setup in 2 minutes</p>
              </div>
            </div>
          </div>
        </div>
      </section>
     
      {/* CTA */}
      <section className="py-16 md:py-24 bg-primary-teaGreen/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-gradient-to-r from-primary-teaGreen/30 to-primary-lightGreen/20 p-8 md:p-12 lg:p-16 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-black mb-4">Ready to Build Your Dream Team?</h2>
            <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">Join hundreds of companies already using Dhoondh to find great talent. Start completely free.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register?isCompany=true">
                <Button size="lg" className="px-8 py-4 text-lg">Start Hiring Free</Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="px-8 py-4 text-lg border-primary-black text-primary-black hover:bg-primary-black hover:text-white">Contact Sales</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CompanyServices;
